interface SCWindow extends Window {
  __storycore_audio_playing?: Record<string, boolean>;
  __storycore_audio_played?: Record<string, boolean>;
  __storycore_audio_muted?: boolean;
  __storycore_narration_muted?: boolean;
}

/**
 * Determines if narration should be considered muted right now.
 * Checks element-level mute, section attribute, HUD button, global flags, and persisted preference.
 */
export function isNarrationMuted(audio: HTMLAudioElement): boolean {
  try {
    // Element-level mute always wins
    if (audio.muted) return true;

    // Per-section overrides set by HUD (highest priority)
    const sectionEl = audio.closest("section") as HTMLElement | null;
    if (sectionEl?.hasAttribute("data-user-muted")) return true;
    if (sectionEl?.hasAttribute("data-user-unmuted")) return false;

    // Section-level flag (seen in markup as data-narration-muted="1")
    const section = audio.closest("section");
    const sectionMuted = section?.getAttribute("data-narration-muted");
    if (sectionMuted && /^(1|true)$/i.test(sectionMuted)) return true;

    // HUD button state (aria-pressed="true" when muted)
    const hudBtn = document.querySelector(".narration-mute-button") as HTMLElement | null;
    if (hudBtn && hudBtn.getAttribute("aria-pressed") === "true") return true;

    // Global flags some code paths may set
    const w = window as SCWindow;
    if (w.__storycore_narration_muted === true || w.__storycore_audio_muted === true) return true;

    // Persistent/global mute state (root attribute or localStorage)
    try {
      const root = document.documentElement;
      const persisted = localStorage.getItem("storycore-audio-muted");
      if ((persisted && persisted !== "0" && persisted.toLowerCase() !== "false") ||
          root.getAttribute("data-audio-muted") === "1") {
        return true;
      }
    } catch {}
  } catch {
    // no-op
  }
  return false;
}

/**
 * Attempts to play a narration audio element with full respect for
 * mute state, autoplay preferences, and de-duplication. Handles lifecycle cleanup.
 *
 * @param audio - The HTMLAudioElement to attempt playback on.
 * @param forcePlay - When true, ignore "already played" and autoplay-off checks (used after user unmute).
 * @returns true if playback started, false if skipped or failed.
 */
export async function playSectionAudio(audio: HTMLAudioElement, forcePlay: boolean = false): Promise<boolean> {
  const src = audio?.src;
  if (!audio || !src) return false;

  // Respect mute state early; do not even attempt playback if muted.
  if (isNarrationMuted(audio)) {
    // If user explicitly unmuted (forcePlay), clear element/section mute once and proceed.
    if (forcePlay) {
      try {
        audio.muted = false;
        const section = audio.closest("section");
        if (section) section.setAttribute("data-narration-muted", "0");
      } catch {}
    } else {
      return false;
    }
  }

  // Respect global autoplay preference unless a one-shot intent is provided on the element
  // or forcePlay was explicitly requested (e.g., after user unmute).
  try {
    const root = document.documentElement;
    const autoPref = (localStorage.getItem("storycore-narration-autoplay") || "").toLowerCase();
    const autoOff = autoPref === "0" || autoPref === "false" || root.getAttribute("data-narration-autoplay") === "0";
    const oneShot =
      (audio as any).__sc_allow_programmatic_play === true ||
      audio.dataset.allowPlay === "1";

    if (autoOff && !oneShot && !forcePlay) return false;

    // consume one-shot flags
    if ((audio as any).__sc_allow_programmatic_play) {
      try { delete (audio as any).__sc_allow_programmatic_play; } catch {}
    }
    if (audio.dataset.allowPlay === "1") {
      try { delete audio.dataset.allowPlay; } catch {}
    }
  } catch {}

  const w = window as SCWindow;
  w.__storycore_audio_playing = w.__storycore_audio_playing || {};
  w.__storycore_audio_played  = w.__storycore_audio_played  || {};

  // From here on, treat these maps as non-nullable.
  const playing: Record<string, boolean> = w.__storycore_audio_playing!;
  const played:  Record<string, boolean> = w.__storycore_audio_played!;

  // Only handle loop flag and safe playback here — no mute logic lives in this file.
  try {
    const rawLoop = audio.getAttribute("loop");
    // true if: boolean attribute present with no value (<audio loop> → ""), or set to "true"/"1"
    audio.loop = rawLoop === "" || rawLoop === "true" || rawLoop === "1";
  } catch {}

  // Avoid replays for non-looping audio if it's already playing/played,
  // unless forcePlay is explicitly requested (e.g., after unmute).
  const alreadyHandled = playing[src] || played[src] || audio.ended;
  if (alreadyHandled && !audio.loop && (!forcePlay || audio.ended)) return false;

  // Ensure unmuted only if we're not muted by HUD/flags; higher-level code decides allowance.
  try { if (!isNarrationMuted(audio)) audio.muted = false; } catch {}

  // Always restart non-looping audio from the beginning when (re)starting.
  if (!audio.loop) {
    try { audio.currentTime = 0; } catch {}
  }

  playing[src] = true;

  const clearPlaying = () => {
    try { delete (w.__storycore_audio_playing as Record<string, boolean>)[src]; } catch {}
  };
  const markPlayed = () => {
    try {
      w.__storycore_audio_played = { ...(w.__storycore_audio_played as Record<string, boolean>), [src]: true };
    } catch {}
  };

  audio.addEventListener("ended", () => { markPlayed(); clearPlaying(); }, { once: true });
  audio.addEventListener("pause", () => { clearPlaying(); }, { once: true });
  audio.addEventListener("error", () => { clearPlaying(); }, { once: true });
  audio.addEventListener("emptied", () => { clearPlaying(); }, { once: true });
  audio.addEventListener("abort", () => { clearPlaying(); }, { once: true });

  try {
    await audio.play();
    try { (window as SCWindow).__storycore_narration_muted = false; } catch {}
    return true;
  } catch (err) {
    console.warn("[AUDIO] Playback failed for", src, err);
    clearPlaying();
    try {
      const sectionEl = audio.closest("section") as HTMLElement | null;
      if (sectionEl) {
        sectionEl.dataset.audioStarted = "0";
        sectionEl.setAttribute("data-narration-muted", "1");
        sectionEl.removeAttribute("data-user-unmuted");

        sectionEl
          .querySelectorAll<HTMLVideoElement>("video[data-role='background'], video[data-bg='true']")
          .forEach((v) => {
            try { v.pause(); } catch {}
            try {
              v.muted = true;
              v.setAttribute("muted", "");
              v.volume = 0;
            } catch {}
          });
      }
      (window as SCWindow).__storycore_narration_muted = true;
    } catch {}
    return false;
  }
}

/**
 * Clear per-section narration flags so scroll/autoplay logic won't be blocked by stale attributes.
 * - Removes data-narration-muted and data-user-muted
 * - Sets data-user-unmuted="1" (so isNarrationMuted() treats section as explicitly unmuted)
 */
export function clearSectionNarrationFlags(section: HTMLElement): void {
  try {
    section.removeAttribute("data-narration-muted");
    section.removeAttribute("data-user-muted");
    section.setAttribute("data-user-unmuted", "1");
  } catch {}
}

/**
 * Clear narration flags across all observed sections.
 */
export function clearAllSectionNarrationFlags(root: ParentNode = document): void {
  try {
    const nodes = root.querySelectorAll<HTMLElement>('section[data-section-index]');
    nodes.forEach((s) => clearSectionNarrationFlags(s));
  } catch {}
}
